import { useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';

import { Button } from '@zendesk/forethought-elements';
import LandingPageLayout from 'src/components/reusable-components/landing-page-layout';
import { getCurrentUser } from 'src/services/apiV1';
import { basePath } from 'src/utils/basePath';
import {
  buildCliLoginPayload,
  clearStashedCliLoginRequest,
  getCliLoginRequest,
  isValidCliRedirectUri,
  submitCliLoginForm,
} from 'src/utils/cliLoginUtils';
import { UserRoles } from 'src/utils/enums';

const CLI_ROLES: string[] = [UserRoles.ROLE_ADMIN, UserRoles.ROLE_SUPER_ADMIN];

type Status = 'loading' | 'ready' | 'notAdmin' | 'canceled' | 'error';

export default function CliLogin() {
  const { t } = useTranslation('authentication-wall');
  const { search } = useLocation();
  const navigate = useNavigate();
  const request = useMemo(() => getCliLoginRequest(search), [search]);
  const isValidRequest =
    request !== null && isValidCliRedirectUri(request.redirectUri);
  const [status, setStatus] = useState<Status>('loading');
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    clearStashedCliLoginRequest();
    if (!isValidRequest) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const session = await Auth.currentSession();
        const claims = session.getIdToken().payload;
        const groups: string[] = claims['cognito:groups'] ?? [];
        if (cancelled) {
          return;
        }
        setEmail(String(claims.email ?? ''));
        if (!groups.some(group => CLI_ROLES.includes(group))) {
          setStatus('notAdmin');
          return;
        }
        setStatus('ready');
        const { organization } = await getCurrentUser();
        if (!cancelled) {
          setOrgName(organization?.display_name ?? '');
        }
      } catch (err) {
        Sentry.captureException(err);
        if (!cancelled) {
          setStatus(current => (current === 'ready' ? current : 'error'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isValidRequest]);

  const handleApprove = async () => {
    if (!request) {
      return;
    }
    setIsSubmitting(true);
    try {
      const session = await Auth.currentSession();
      const payload = buildCliLoginPayload({
        apiUrl: API_URL,
        authConfig: Auth.configure(),
        basePath,
        email,
        idToken: session.getIdToken().getJwtToken(),
        origin: window.location.origin,
        refreshToken: session.getRefreshToken().getToken(),
        state: request.state,
      });
      submitCliLoginForm(request.redirectUri, request.state, payload);
    } catch (err) {
      Sentry.captureException(err);
      setIsSubmitting(false);
      setStatus('error');
    }
  };

  const renderMessage = (message: string) => (
    <>
      <Box
        component='p'
        sx={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-ms)',
          lineHeight: '21px',
        }}
      >
        {message}
      </Box>
      <Button fullWidth onClick={() => navigate('/')} variant='secondary'>
        {t('cliLogin.backToDashboard')}
      </Button>
    </>
  );

  const renderBody = () => {
    if (!isValidRequest) {
      return renderMessage(t('cliLogin.invalidRequest'));
    }
    switch (status) {
      case 'loading':
        return null;
      case 'notAdmin':
        return renderMessage(t('cliLogin.adminsOnly'));
      case 'canceled':
        return renderMessage(t('cliLogin.canceled'));
      case 'error':
        return renderMessage(t('cliLogin.sessionError'));
      case 'ready':
        return (
          <>
            <Box
              component='p'
              sx={{
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-ms)',
                lineHeight: '21px',
              }}
            >
              <Trans
                components={{ bold: <b /> }}
                i18nKey={orgName ? 'cliLogin.body' : 'cliLogin.bodyNoOrg'}
                t={t}
                values={{ email, org: orgName }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                disabled={isSubmitting}
                fullWidth
                onClick={() => void handleApprove()}
                variant='main'
              >
                {t('cliLogin.approve')}
              </Button>
              <Button
                disabled={isSubmitting}
                fullWidth
                onClick={() => setStatus('canceled')}
                variant='secondary'
              >
                {t('cliLogin.cancel')}
              </Button>
            </Box>
          </>
        );
    }
  };

  return (
    <LandingPageLayout>
      <>
        <Box
          sx={{
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-semibold)',
          }}
        >
          {t('cliLogin.title')}
        </Box>
        {renderBody()}
      </>
    </LandingPageLayout>
  );
}
